import React from 'react';
import { Save, Wallet, Truck, Users, Calendar, Star } from 'lucide-react';

const SLOT_KEYS = ['slot1', 'slot2', 'slot3'];

const fmtMoney = (n) => {
  if (typeof n !== 'number') return '—';
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k €`;
  return `${Math.round(n)} €`;
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return null; }
};

/**
 * Supervisor-facing read-only view of the 3 client-side save slots.
 * Renders 3 boxes (used or empty); used slots show name + headline KPIs.
 * Used in SupervisionView (per-apprentice card) and the admin GlobalSupervision DetailPanel.
 */
export default function SaveSlotsBlock({ saveSlots, testIdPrefix = 'save-slots' }) {
  const byKey = (saveSlots || []).reduce((acc, s) => {
    if (s && s.slot_key) acc[s.slot_key] = s;
    return acc;
  }, {});
  const usedCount = SLOT_KEYS.filter((k) => byKey[k]).length;
  // 17/07/2026 — le slot « favori » (⭐ choisi par l'apprenant) est présenté
  // en premier, mis en avant visuellement.
  const orderedKeys = [...SLOT_KEYS].sort(
    (a, b) => ((byKey[b]?.favorite ? 1 : 0) - (byKey[a]?.favorite ? 1 : 0)),
  );
  return (
    <div data-testid={testIdPrefix}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5 flex items-center gap-1">
        <Save className="w-3 h-3" /> Sauvegardes ({usedCount}/3)
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {orderedKeys.map((key) => {
          const slot = byKey[key];
          const idx = Number(key.replace('slot', ''));
          return slot
            ? <UsedSlot key={key} slot={slot} idx={idx} testid={`${testIdPrefix}-${key}`} />
            : <EmptySlot key={key} idx={idx} testid={`${testIdPrefix}-${key}-empty`} />;
        })}
      </div>
    </div>
  );
}

function UsedSlot({ slot, idx, testid }) {
  const dateStr = fmtDate(slot.saved_at);
  const fav = !!slot.favorite;
  return (
    <div
      className={`rounded-md border px-2.5 py-2 text-[11px] space-y-1 ${
        fav ? 'border-amber-400/70 bg-amber-500/10' : 'border-money/40 bg-money/5'
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className={`font-bold font-mono text-[10px] flex items-center gap-1 ${fav ? 'text-amber-300' : 'text-money'}`}>
          {fav && <Star className="w-3 h-3 fill-amber-400 text-amber-400" data-testid={`${testid}-favorite-star`} />}
          SLOT {idx}{fav ? ' · FAVORI' : ''}
        </span>
        {dateStr && <span className="text-[9px] text-slate-500 font-mono">{dateStr}</span>}
      </div>
      {/* 18/07/2026 — note pédagogique de l'apprenant sur son slot favori. */}
      {fav && slot.favorite_note && (
        <p
          className="text-[10px] text-amber-200/90 italic leading-snug"
          title={slot.favorite_note}
          data-testid={`${testid}-favorite-note`}
        >
          « {slot.favorite_note} »
        </p>
      )}
      <p className="text-slate-200 font-semibold truncate" title={slot.name || ''}>
        {slot.name || 'Partie sans nom'}
      </p>
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] text-slate-400 font-mono">
        <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />J{slot.total_days || 0}</span>
        <span className="flex items-center gap-1"><Wallet className="w-2.5 h-2.5" />{fmtMoney(slot.treasury)}</span>
        <span className="flex items-center gap-1"><Truck className="w-2.5 h-2.5" />{slot.vehicle_count || 0} véh.</span>
        <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5" />{slot.driver_count || 0} ch.</span>
      </div>
    </div>
  );
}

function EmptySlot({ idx, testid }) {
  return (
    <div
      className="rounded-md border border-dashed border-ink-600 bg-ink-800/40 px-2.5 py-2 text-[11px] text-slate-600 flex items-center justify-center"
      data-testid={testid}
    >
      <span className="font-mono text-[10px]">SLOT {idx} · vide</span>
    </div>
  );
}
